import {
  calculatePoints,
  findLastInStack,
  getMapFromStack,
  questionAnswers,
  questionHasDisclaimer,
  selectDisplayedOrAllGroupQuestions,
  selectFinal,
  selectGroup,
  selectQuestion,
  selectRelevantQuestions,
  selectWelcome,
  sortGroupQuestionsByStack,
} from 'components-machine';
import { getQuestionGroupId, selectQuestionDeck } from 'components-machine/src/selects';
import { uniq } from 'lodash';
import React, { FC, ReactNode, Suspense, useContext, useEffect, useMemo, useRef } from 'react';
import { Group, Poll, Question } from 'schema/poll';
import { Poll as WebviewPoll } from 'schema/webview';
import { calcPollMaxAwardAmount } from 'shared/src/utils/award';
import { AuthErrorOverlay } from '../../components/AuthErrorOverlay';
import { ConnectionErrorOverlay } from '../../components/ConnectionErrorOverlay';
import { InitPollErrorOverlay } from '../../components/InitPollErrorOverlay';
import { NewConnectionErrorOverlay } from '../../components/NewConnectionErrorOverlay';
import { useAuthContext } from '../../contexts/authContext';
import { RootContext } from '../../contexts/rootContext';
import { useUpdateEffect } from '../../hooks/useUpdateEffect';
import {
  usePollContextValue,
  usePollStatePredicate,
  usePollTransitionAbilityPredicate,
} from '../../reducer/machine/utils';
import WebviewContextWrapper from '../../store/WebviewContextWrapper';
import withContext, { PollContext, WithContextInject } from '../../store/withContext';
import { ICState } from '../../types/domain';
import { CompositeQuestionProps, Poll as PollType, QuestionGroupProps, QuestionProps } from '../../types/poll.domain';
import { notifyParent } from '../../utils/notifyIframe';
import { FinalProps } from '../poll/finals/Final/Final';
import { WelcomeProps } from '../webview/welcomes/Welcome/Welcome';
import { useICStateActions } from './ICStateActionsContext';
import { PollRenderAutosizeScrollContainer } from './PollAutosizeScrollContainer';
import { QuestionTitleProvider } from './QuestionTitleContext';
import { ReadyStateChangeMarker } from './ReadyStateChangeMarker';
import { PrefetchTags } from './resourcePreload';
import { PollTimer } from './TimerContext';
import { useTrackingContext } from './TrackingContext';
import {
  getProgress,
  getProgressBar,
  GroupQuestionWrapperProps,
  OuterGroupWrapperProps,
  WrapperProps,
} from './wrapper';

type OwnProps = {
  poll: PollType;
  title: string | null;
};

type Props = WithContextInject & OwnProps;

interface PollRenderProps<T extends Poll | WebviewPoll> {
  state: ICState;
  poll: PollType;
  title: string | null;
  isLoading: boolean;
  renderers: Renderers<T>;
}

const PollRender = <T extends Poll | WebviewPoll>({ state, renderers, poll, title, isLoading }: PollRenderProps<T>) => {
  usePollScreenChangedIframeNotifier(state);

  useEffect(() => {
    // setTimeout to wait for the first question to render and trigger first promises
    setTimeout(() => {
      for (const type of uniq(poll.questions.map(v => v.data.type))) {
        renderers.preloadQuestion?.(type);
      }
    }, 100);
  }, []);

  const { questionId, groupId, finalId, welcomeId } = state;
  const points = calculatePoints(state.answersStack);
  const maxPoints = calcPollMaxAwardAmount(poll.questions, state.answersStack);
  const showBackButtonPredicate = usePollTransitionAbilityPredicate({ type: 'back' });
  const prevShowBackButton = useRef<boolean>(showBackButtonPredicate);
  const showBackButton = isLoading ? prevShowBackButton.current : showBackButtonPredicate;

  prevShowBackButton.current = showBackButton;
  const wrapperProps = useWrapperProps(title, poll, state, isLoading);

  if (finalId) {
    return (
      <FinalRender
        renderers={renderers}
        title={title}
        points={points}
        poll={poll}
        maxPoints={maxPoints}
        finalId={finalId}
      />
    );
  }

  if (welcomeId) {
    return <WelcomeRender renderers={renderers} title={title} welcomeId={welcomeId} poll={poll} />;
  }

  const deck = groupId ? poll.questionDecks.find(deck => deck.groupId === groupId) : null;

  if (groupId && deck?.data.type === 'card_sorting') {
    return (
      <CompositeQuestionRender
        groupId={groupId}
        deckId={deck.id}
        poll={poll}
        title={title}
        icState={state}
        wrapperProps={wrapperProps}
        renderers={renderers}
      />
    );
  }

  if (groupId) {
    return (
      <GroupRender
        renderers={renderers}
        questionId={questionId}
        groupId={groupId}
        poll={poll}
        state={state}
        wrapperProps={wrapperProps}
      />
    );
  }

  if (questionId) {
    return (
      <QuestionRender
        title={title}
        questionId={questionId}
        poll={poll as T}
        icState={state}
        wrapperProps={wrapperProps}
        renderers={renderers}
      />
    );
  }

  return null;
};

function useWrapperProps(title: string | null, poll: PollType, state: ICState, isLoading: boolean) {
  const { questionId, groupId, answersStack } = state;

  const answersMap = getMapFromStack(answersStack);

  const { schemaEngine } = useContext(RootContext);

  const { textFieldDisclaimer } = poll;

  const question = questionId ? selectQuestion(poll, questionId) : null;
  const group = groupId ? selectGroup(poll, groupId) : null;

  const relevantQuestions = selectRelevantQuestions({ state, poll });

  const hasTextField = relevantQuestions.some(question => questionHasDisclaimer(question, answersMap[question.id]));
  const hasMultipleAnswerQuestion = relevantQuestions.some(question =>
    schemaEngine.hasTrait(question.data.type, 'MULTIPLE_ANSWERS_TYPE'),
  );

  const isSequentialDisplayGroup = group ? group.data.hasSequentialDisplay : undefined;

  const isQuestionAnswered = (question: Question) => {
    const answerState = answersMap[question.id];
    const isAnswered =
      !!answerState?.isWithoutAnswers || !!answerState?.answeredIds?.length || !!answerState?.answeredOthers?.length;
    return isAnswered;
  };

  const isQuestionOptional = (question: Question) => {
    return !('requiresAnswer' in question.data && !!question.data.requiresAnswer);
  };

  const isAllRequiredQuestionsAnswered = relevantQuestions.every(question => {
    return isQuestionOptional(question) || isQuestionAnswered(question);
  });

  const isSomeQuestionsAnswered = relevantQuestions.some(isQuestionAnswered);
  const isAllQuestionsAnswered = relevantQuestions.every(isQuestionAnswered);
  const isAllQuestionsOptional = relevantQuestions.every(isQuestionOptional);

  const progressBar = getProgressBar(poll);
  const progress = getProgress({ poll, icState: state });

  const showBackButton = usePollTransitionAbilityPredicate({ type: 'back' });
  const { onNext, onBack, onClose, onSkip, onAnswer, onTryAnswer } = useICStateActions();

  const wrapperProps: Omit<WrapperProps, 'children'> = {
    title,
    hasTextField,
    hasMultipleAnswerQuestion,
    isAllQuestionsOptional,
    isAllQuestionsAnswered,
    isSomeQuestionsAnswered,
    isAllRequiredQuestionsAnswered,
    isSequentialDisplayGroup,
    progressBar,
    textFieldDisclaimer,
    isLoading,
    progress,
    alignSurvey: poll.alignSurvey ?? 'left',
    showBackButton,
    hasWelcome: !!poll.welcome?.length,
    isWebview: poll.template === 'webview' || poll.template === 'dialog_webview',
    systemButtonText: group ? group.data.systemButtonText : question?.data.systemButtonText,
    autoCommit:
      group && 'autoCommit' in group.data
        ? !!group.data.autoCommit
        : question && 'autoCommit' in question.data
          ? !!question.data.autoCommit
          : false,
    onNext,
    onBack,
    onClose,
    onSkip,
    onAnswer,
    onTryAnswer,
  };
  return wrapperProps;
}

function FinalRender<T extends Poll | WebviewPoll>({
  poll,
  finalId,
  points,
  maxPoints,
  title,
  renderers,
}: {
  poll: PollType;
  finalId: string;
  points: number;
  maxPoints: number;
  title: string | null;
  renderers: Renderers<T>;
}) {
  const { onFinalClick } = useICStateActions();

  const final = selectFinal(poll, finalId);
  if (!final) return null;

  const FinalComponent = (
    renderers.Final as unknown as Record<
      string,
      React.FC<{
        title: string | null;
        points: number;
        maxPoints: number;
        final: T['finals'][number]['data'];
        onFinalClick: () => void;
        awardType: 'AWARD_POINTS' | 'AWARD_SCORES' | undefined;
        hasAuthQuestion: boolean;
      }>
    >
  )[final.data.type];
  if (!FinalComponent) return null;
  const hasAuthQuestion = poll.questions.some(question => question.data.type === 'POLL_AUTH');
  return (
    <FinalComponent
      title={title}
      points={points}
      maxPoints={maxPoints}
      final={final.data}
      awardType={poll.awardType}
      onFinalClick={() => {
        onFinalClick(finalId);
      }}
      hasAuthQuestion={hasAuthQuestion}
    />
  );
} // TODO: заменить на switch в entrypoint

function WelcomeRender<T extends Poll | WebviewPoll>({
  poll,
  title,
  welcomeId,
  renderers,
}: {
  poll: PollType;
  welcomeId: string;
  title: string | null;
  renderers: Renderers<T>;
}) {
  const { onWelcomeClick, onClose } = useICStateActions();

  const welcome = selectWelcome(poll, welcomeId);

  if (!welcome) return null;

  const WelcomeComponent = (
    renderers.Welcome as unknown as Record<
      string,
      React.FC<{
        title: string | null;
        welcome: NonNullable<T['welcome']>[number]['data'];
        onClick: () => void;
        onClose: () => void;
      }>
    >
  )[welcome.data.type];
  if (!WelcomeComponent) return null;
  return <WelcomeComponent title={title} welcome={welcome.data} onClick={onWelcomeClick} onClose={onClose} />;
} // TODO: заменить на switch в entrypoint

function GroupRender<T extends Poll | WebviewPoll>({
  groupId,
  poll,
  state,
  wrapperProps,
  renderers,
}: {
  groupId: string;
  state: ICState;
  poll: PollType;
  questionId: string | null;
  wrapperProps: Omit<WrapperProps, 'children'>;
  renderers: Renderers<T>;
}) {
  const getQuestionGroupProps = (
    poll: PollType,
    state: ICState,
    { groupId, deckId }: { groupId: string; deckId: string | undefined },
  ): QuestionGroupProps => {
    const answersMap = getMapFromStack(state.answersStack);

    const group = selectGroup(poll, groupId);
    const deck = deckId ? selectQuestionDeck(poll, deckId) : undefined;

    const questions = selectDisplayedOrAllGroupQuestions(poll, groupId, state.answersStack);

    const firstGroupQuestion = questions[0]!;
    const answerState = answersMap[firstGroupQuestion.id];
    const firstGroupQuestionData = {
      ...firstGroupQuestion.data,
      answers: questionAnswers({ question: firstGroupQuestion, answerState }),
    } as typeof firstGroupQuestion.data; // dependent types

    const errorMessage = selectRelevantQuestions({ state, poll })
      .map(q => findLastInStack(state.answersStack, q.id).errorMessage)
      .find(e => !!e);

    return {
      group: { ...group.data, id: groupId },
      deck: deck ? { ...deck.data } : undefined,
      firstQuestion: firstGroupQuestionData,
      questionsCount: questions.length,
      errorMessage,
      relevantQuestions: questions,
    };
  };

  const group = selectGroup(poll, groupId);
  const deck = groupId ? poll.questionDecks.find(deck => deck.groupId === groupId) : null;
  const groupQuestions = selectDisplayedOrAllGroupQuestions(poll, groupId, state.answersStack);

  const questionGroupProps = getQuestionGroupProps(poll, state, { groupId, deckId: deck?.id });

  const questions = sortGroupQuestionsByStack(groupId, poll, state.answersStack)
    .filter(Boolean)
    .map((question, idx) => {
      const { id: questionId } = question;
      const isLast = idx === groupQuestions.length - 1;
      const isFirstPollQuestion = idx === 0;
      const answerState = findLastInStack(state.answersStack, questionId);
      const withoutTitle = deck?.data.type === 'matrix' ? !deck.data.text : !group.data.text;
      const withoutQuestionText = !question.data.text;

      return (
        <renderers.InnerGroupWrapper
          key={questionId}
          questionId={questionId}
          questionIndexAtGroup={idx}
          question={
            {
              ...question.data,
              answers: questionAnswers({ question, answerState }),
            } as typeof question.data
          } // maybe typecast can be excluded with dependent types?
          isLast={isLast}
          deck={deck?.data}
          alignSurvey={poll.alignSurvey ?? 'left'}
          isFirstPollQuestion={isFirstPollQuestion}
          withoutTitle={withoutTitle}
          withoutQuestionText={withoutQuestionText}
          error={!!questionGroupProps.errorMessage}
          answerState={answerState}
        >
          <BareQuestionRender
            isFirstPollQuestion={isFirstPollQuestion}
            questionId={questionId}
            poll={poll as T}
            icState={state}
            groupId={groupId}
            renderers={renderers}
            {...wrapperProps}
          />
        </renderers.InnerGroupWrapper>
      );
    });

  return (
    <renderers.OuterGroupWrapper key={groupId} {...wrapperProps} {...questionGroupProps} deck={deck?.data}>
      {questions}
    </renderers.OuterGroupWrapper>
  );
}

function QuestionRender<T extends Poll | WebviewPoll>({
  questionId,
  poll,
  icState,
  wrapperProps,
  renderers,
}: {
  title: string | null;
  questionId: string;
  icState: ICState;
  poll: T;
  wrapperProps: Omit<WrapperProps, 'children'>;
  renderers: Renderers<T>;
}) {
  const question = selectQuestion(poll, questionId);

  const questionProps = useQuestionProps({ questionId, poll, icState });
  const questionWrapperProps = {
    ...wrapperProps,
    ...questionProps,
    questionType: question.data.type,
    set: poll.sets?.find(s => s.id === question.setId),
  };
  const QuestionWrapper = renderers.QuestionWrapper;

  return (
    <QuestionWrapper key={questionId} {...questionWrapperProps}>
      <BareQuestionRender
        renderers={renderers}
        {...questionWrapperProps}
        questionId={questionId}
        poll={poll}
        icState={icState}
      />
    </QuestionWrapper>
  );
}

function CompositeQuestionRender<T extends Poll | WebviewPoll>({
  poll,
  icState,
  wrapperProps,
  groupId,
  deckId,
  renderers,
}: {
  title: string | null;
  groupId: string;
  deckId: string;
  icState: ICState;
  poll: PollType | WebviewPoll;
  wrapperProps: Omit<WrapperProps, 'children'>;
  renderers: Renderers<T>;
}) {
  const questionProps = useCompositeQuestionProps({ poll, icState, groupId, deckId });
  const [firstQuestion] = questionProps.questions;

  const compositeRenderers: Record<
    string,
    FC<CompositeQuestionProps & Omit<WrapperProps, 'children'>>
  > = renderers.CompositeQuestions;
  const QuestionComponent = useMemo(() => compositeRenderers[firstQuestion.type], [firstQuestion.type]);
  if (!QuestionComponent) return null;
  const QuestionWrapper = renderers.QuestionWrapper;

  return (
    <QuestionWrapper key={groupId} questionType={firstQuestion.type} question={firstQuestion} {...wrapperProps}>
      <QuestionComponent {...wrapperProps} {...questionProps} />
    </QuestionWrapper>
  );
}

/** Компонент вопроса без контейнера (контейнера единичного вопроса, группы или матрицы) */
function BareQuestionRender<T extends Poll | WebviewPoll>({
  questionId,
  poll,
  icState,
  isFirstPollQuestion,
  groupId,
  renderers,
  ...wrapperProps
}: {
  isFirstPollQuestion?: boolean;
  questionId: string;
  icState: ICState;
  poll: T;
  groupId?: string;
  renderers: Renderers<T>;
} & Omit<WrapperProps, 'children'>) {
  const questionProps = useQuestionProps({ poll, icState, questionId });

  const question = selectQuestion<T>(poll, questionId);
  const questionRenderers: Record<
    string,
    FC<QuestionProps & { groupId?: string } & Omit<WrapperProps, 'children'>>
  > = renderers.Questions;
  const QuestionComponent = useMemo(() => questionRenderers[question.data.type], [question.data.type]);
  if (!QuestionComponent) return null;
  return (
    <QuestionComponent
      groupId={groupId}
      {...questionProps}
      {...wrapperProps}
      isFirstPollQuestion={!!isFirstPollQuestion}
    />
  );
}

const useQuestionProps = <
  T extends { alignSurvey?: 'left' | 'center'; questions: { id: string; data: object }[]; groups: Group[] },
>({
  questionId,
  poll,
  icState,
}: {
  questionId: string;
  poll: T;
  icState: ICState;
}): QuestionProps => {
  const { onChange, onFileUpload, onOthersChange, onMediaProgressChange, onSkip, onNext, onAnswer, onQuestionFocus } =
    useICStateActions();

  const answersMap = getMapFromStack(icState.answersStack);
  const answerState = answersMap[questionId];

  const question = selectQuestion(poll, questionId);
  const group = icState.groupId ? selectGroup(poll, icState.groupId).data : undefined;

  const questionData = {
    ...question.data,
    answers: questionAnswers({ question, answerState }),
  } as typeof question.data;

  const {
    answeredIds,
    errorMessage,
    mediaProgress,
    answeredOthers,
    isCorrect = false,
    isAnswered = false,
    isSkipped = false,
    answersOrder = [],
  } = answerState ?? {};

  const isFirst = icState.answersStack.findIndex(state => state.id === questionId) === 0;

  return {
    isFirstPollQuestion: false, // TODO: check
    questionId,
    isFirst,
    isAnswered,
    isSkipped,
    question: questionData,
    group,
    isCorrect,
    answeredIds,
    answeredOthers,
    errorMessage,
    mediaProgress,
    answersOrder,
    alignSurvey: poll.alignSurvey ?? 'left',
    onSkip,
    onNext,
    onAnswer,
    onFocus: onQuestionFocus,
    onChange: onChange(questionId),
    onOthersChange: onOthersChange(questionId),
    onFileUpload: onFileUpload(questionId),
    onMediaProgressChange: onMediaProgressChange(questionId),
  };
};

const useCompositeQuestionProps = ({
  poll,
  icState,
  groupId,
  deckId,
}: {
  groupId: string;
  deckId: string;
  poll: PollType | WebviewPoll;
  icState: ICState;
}): CompositeQuestionProps => {
  const { onChange, onOthersChange, onAnswer, onSkip } = useICStateActions();

  const answersMap = getMapFromStack(icState.answersStack);

  const questions = selectDisplayedOrAllGroupQuestions<PollType['questions'][0] | WebviewPoll['questions'][0]>(
    poll,
    groupId,
    icState.answersStack,
  )
    .filter(question => question.deckId === deckId)
    .map(question => {
      const state = findLastInStack(icState.answersStack, question.id);

      return {
        id: question.id,
        ...question.data,
        errorMessage: state.errorMessage,
        answeredIds: state.answeredIds ?? [],
        answers: questionAnswers({ question, answerState: answersMap[question.id] }),
      };
    });

  const deck = selectQuestionDeck(poll, deckId);

  const handleChange = ({
    questionId,
    ...answers
  }: {
    questionId: string;
    answeredIds: string[];
    answersOrder?: number[];
  }) => {
    onChange(questionId)(answers);
  };

  const handleOthersChange = ({ questionId, value }: { questionId: string; value: string }) => {
    onOthersChange(questionId)({ value });
  };

  return {
    text: deck.data.text ?? '',
    groupId,
    questions,
    onChange: handleChange,
    onOthersChange: handleOthersChange,
    onAnswer,
    onSkip,
  };
};

function PollLoadAndErrorContainer<T extends Poll | WebviewPoll>(
  props: Props & {
    renderers: Renderers<T>;
  },
) {
  const { poll, title } = props;

  const { onClose } = useICStateActions();
  const { isSubmittingTracking, trackingError } = useTrackingContext();
  const isLoadingNextData = usePollStatePredicate('questions.screen.answered.loadingNextData');
  const hasAuthStateLoadingError = usePollStatePredicate('fetchingAuthState.error');
  const isRetryingAuthStateLoading = usePollStatePredicate('fetchingAuthState.retrying');
  const { authUser, refreshAuthUser } = useAuthContext();

  const { handleRetry: retryTracking, retryCount } = useTrackingContext();
  const { interpreter } = useContext(PollContext);

  const isNextCalculatorUnauthorizedError = usePollStatePredicate(
    'questions.screen.tracking.nextCalculatorUnauthorizedError',
  );

  const isInstantlyAutoClosed = usePollStatePredicate('instantlyAutoClosed');
  const isLoadingNextCalculator = usePollStatePredicate('questions.screen.tracking.calculatingNext');
  const nextCalculatorError = usePollContextValue('nextCalculatorError');
  const hasNextCalculatorError = !!nextCalculatorError;

  const initServerPassPollError = usePollContextValue('initServerPassPollError');
  const hasInitServerPassPollError = !!initServerPassPollError;

  const isLoading = isSubmittingTracking || isLoadingNextData || isInstantlyAutoClosed;
  const hasTrackingError = !!trackingError;

  const isWebview = poll.template === 'webview';

  const showAuthOverlay = hasAuthStateLoadingError || isRetryingAuthStateLoading;

  return (
    <WebviewContextProvider poll={poll} state={props.state}>
      <QuestionTitleProvider>
        <PollTimer poll={poll} state={props.state}>
          <PrefetchTags poll={poll} icState={props.state} />
          <PollRenderAutosizeScrollContainer poll={poll} icState={props.state}>
            {!showAuthOverlay && isNextCalculatorUnauthorizedError && (
              <AuthErrorOverlay
                isWebview={isWebview}
                onRetry={refreshAuthUser}
                isLoading={isRetryingAuthStateLoading}
              />
            )}
            {showAuthOverlay && (
              <AuthErrorOverlay isLoading={isRetryingAuthStateLoading} isWebview={isWebview} onRetry={authUser} />
            )}
            {hasTrackingError && (
              <ErrorOverlay
                onClose={onClose}
                isWebview={isWebview}
                isLoading={isLoading}
                retryCount={retryCount}
                handleRetry={retryTracking}
                template={poll.template}
              />
            )}
            {hasNextCalculatorError && (
              <ErrorOverlay
                onClose={onClose}
                isWebview={isWebview}
                retryCount={retryCount}
                isLoading={isLoadingNextCalculator}
                handleRetry={() => {
                  interpreter.send({ type: 'retryNextCalculator' });
                }}
                template={poll.template}
              />
            )}
            {hasInitServerPassPollError && (
              <InitPollErrorOverlay
                onClose={onClose}
                isWebview={isWebview}
                isLoading={isLoadingNextCalculator}
                onRetry={() => {
                  interpreter.send({ type: 'retryInitServerPassPoll' });
                }}
              />
            )}

            <Suspense fallback={null}>
              <ReadyStateChangeMarker />
              {!(showAuthOverlay || hasTrackingError || hasNextCalculatorError || hasInitServerPassPollError) && (
                <PollRender
                  renderers={props.renderers}
                  title={title}
                  poll={poll}
                  state={props.state}
                  isLoading={isLoading}
                />
              )}
            </Suspense>
          </PollRenderAutosizeScrollContainer>
        </PollTimer>
      </QuestionTitleProvider>
    </WebviewContextProvider>
  );
}

function ErrorOverlay({
  handleRetry,
  retryCount,
  isWebview,
  isLoading,
  onClose,
  template,
}: {
  isWebview: boolean;
  isLoading: boolean;
  retryCount: number;
  onClose: VoidFunction;
  handleRetry: VoidFunction;
  template: string;
}) {
  if (template === 'poll') {
    return (
      <NewConnectionErrorOverlay isLoading={isLoading} onRetry={handleRetry} isWebview={isWebview} onClose={onClose} />
    );
  }

  return (
    <ConnectionErrorOverlay isLoading={isLoading} isWebview={isWebview} onRetry={handleRetry} retryCount={retryCount} />
  );
}

function usePollScreenChangedIframeNotifier(state: ICState) {
  const { questionId, groupId, finalId, welcomeId } = state;

  useUpdateEffect(() => {
    if (welcomeId || questionId || groupId || finalId) notifyParent({ type: 'survey_iframe_screen_changed' }); // FIXME: move to machine!
  }, [welcomeId, questionId, groupId, finalId]);
}

export const getTextSize = () => {
  //TODO:FIX

  return 'M' as const;
};

function WebviewContextProvider({ children, poll, state }: { children: ReactNode; state: ICState; poll: PollType }) {
  const canGoToPreviousQuestion = (() => {
    const [firstQuestion] = poll.questions;

    const { canGoBack } = poll as unknown as WebviewPoll;

    const firstQuestionGroupId = firstQuestion ? getQuestionGroupId(firstQuestion, poll.questionDecks) : undefined;
    const isAtFirstQuestion = state.questionId === firstQuestion?.id || state.groupId === firstQuestionGroupId;
    return !!(canGoBack && !isAtFirstQuestion && (state.groupId || state.questionId));
  })();

  // TODO Исправить тип poll
  const { closeable } = poll as unknown as WebviewPoll;

  const progressBar = getProgressBar(poll);
  const showProgressBar = progressBar !== 'invisible';
  const textSize = getTextSize();

  return (
    <WebviewContextWrapper
      textSize={textSize}
      canGoBack={!!poll.canGoBack}
      canGoToPreviousQuestion={canGoToPreviousQuestion}
      closeable={closeable}
      showProgressBar={showProgressBar}
    >
      {children}
    </WebviewContextWrapper>
  );
}

type CompositeQuestionTypes = 'POLL_CARD_SORTING_CATEGORY';
export type QuestionWrapperComponentProps = WrapperProps & {
  questionType: string;
} & Pick<QuestionProps, 'question'>;

export type QuestionComponentProps = QuestionProps & {
  groupId?: string;
} & Omit<WrapperProps, 'children'>;

export type CompositeQuestionComponentProps = CompositeQuestionProps & Omit<WrapperProps, 'children'>;

export type WelcomeComponentProps<
  T extends Partial<
    Record<
      'questions' | 'welcome' | 'finals',
      {
        data: {
          type: string;
        };
      }[]
    >
  >,
  Type extends NonNullable<T['welcome']>[number]['data']['type'],
> = WelcomeProps<
  Extract<
    NonNullable<T['welcome']>[number],
    {
      data: {
        type: Type;
      };
    }
  >
>;
export type FinalComponentProps = FinalProps;

type InnerGroupWrapperComponentProps = GroupQuestionWrapperProps & {
  withoutTitle: boolean;
  error: boolean;
  withoutQuestionText: boolean;
};

type OuterGroupWrapperComponentProps = OuterGroupWrapperProps & {
  firstQuestion: Question['data'];
};

type QuestionTypesExceptComposites<
  T extends Partial<
    Record<
      'questions' | 'welcome' | 'finals',
      {
        data: {
          type: string;
        };
      }[]
    >
  >,
> = Exclude<NonNullable<T['questions']>[number]['data']['type'], CompositeQuestionTypes>;

type CompositeQuestions<
  T extends Partial<
    Record<
      'questions' | 'welcome' | 'finals',
      {
        data: {
          type: string;
        };
      }[]
    >
  >,
> = Extract<NonNullable<T['questions']>[number]['data']['type'], CompositeQuestionTypes>;

type WelcomeTypes<
  T extends Partial<
    Record<
      'questions' | 'welcome' | 'finals',
      {
        data: {
          type: string;
        };
      }[]
    >
  >,
> = NonNullable<T['welcome']>[number]['data']['type'];

type FinalTypes<
  T extends Partial<
    Record<
      'questions' | 'welcome' | 'finals',
      {
        data: {
          type: string;
        };
      }[]
    >
  >,
> = NonNullable<T['finals']>[number]['data']['type'];

export type Renderers<T extends Partial<Record<'questions' | 'welcome' | 'finals', { data: { type: string } }[]>>> = {
  QuestionWrapper: React.FC<QuestionWrapperComponentProps>;
  InnerGroupWrapper: React.FC<InnerGroupWrapperComponentProps>;
  OuterGroupWrapper: React.FC<OuterGroupWrapperComponentProps>;
  Questions: Record<QuestionTypesExceptComposites<T>, React.FC<QuestionComponentProps>>;
  CompositeQuestions: Record<CompositeQuestions<T>, React.FC<CompositeQuestionComponentProps>>;
  Welcome: {
    [Type in WelcomeTypes<T>]: React.FC<WelcomeComponentProps<T, Type>>;
  };
  Final: Record<FinalTypes<T>, React.FC<FinalComponentProps>>;
  preloadQuestion?: (type: string) => void;
};
export function createPollRenderComponent<T extends Poll | WebviewPoll>(renderers: Renderers<T>) {
  return withContext(function PollRenderer(props: Props) {
    return <PollLoadAndErrorContainer renderers={renderers} {...props} />;
  });
}
